import React from "react";
import { Card, Empty } from "../../components/analytics-kit";
import DataShell, { Btn, Notice } from "./shell";

/*
 * Manage Data › Configuration rules on the kit (approved mockup Rules.dc.html, 2026-10-09).
 * Editor state and the API calls live in views/config-rules/index.jsx and arrive as props;
 * this file is the new layout only (the legacy layout stays in index.jsx).
 *
 * Rules read as plain sentences ("SKU starts with A, B or C") parsed from the stored regex
 * where the shape allows; anything richer falls back to "SKU matches <pattern>". The inline
 * form writes the regex back from "starts with / is / contains / ends with" + text, with a
 * "Write a pattern instead" escape hatch. The preview endpoint reports counts per kind only,
 * so there is no per-rule "matched" column; the form's live count previews the one rule alone.
 */

// ---- pattern <-> sentence -------------------------------------------------------------------
const META = ".^$*+?()[]{}|\\";
function unescapeLiteral(s) {
  let out = "";
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === "\\") {
      const n = s[i + 1];
      if (n === undefined) return null;
      if (n === "s") {
        out += " ";
        i++;
        if (s[i + 1] === "+" || s[i + 1] === "*") i++;
        continue;
      }
      if (/[A-Za-z0-9]/.test(n)) return null; // \d, \w, \b … are not literal text
      out += n;
      i++;
      continue;
    }
    if (META.indexOf(c) >= 0) return null;
    out += c;
  }
  return out;
}

/** {op: is|starts|ends|contains, values[], number, editable} or null when the pattern isn't a plain shape. */
export function parsePattern(pattern) {
  let s = String(pattern || "").trim();
  if (!s) return null;
  if (s.startsWith("(?i)")) s = s.slice(4);
  let start = false,
    end = false,
    number = false;
  if (s[0] === "^") {
    start = true;
    s = s.slice(1);
  }
  if (s.endsWith(".*") && !s.endsWith("\\.*")) s = s.slice(0, -2);
  if (s.endsWith("$") && !s.endsWith("\\$")) {
    end = true;
    s = s.slice(0, -1);
  }
  if (start && !end) {
    const nm = s.match(/(\\d|\[0-9\])(\+|\*|\?|\{\d+(,\d*)?\})?$/);
    if (nm) {
      number = true;
      s = s.slice(0, nm.index);
    }
  }
  let alts = [s];
  let altExact = false;
  if (s.startsWith("(") && s.endsWith(")")) {
    let inner = s.slice(1, -1);
    if (inner.startsWith("?:")) inner = inner.slice(2);
    if (/(^|[^\\])[()]/.test(inner)) return null;
    alts = inner.split("|");
  }
  const values = [];
  for (let a of alts) {
    if (start && !end && a.endsWith("$") && !a.endsWith("\\$")) {
      a = a.slice(0, -1);
      altExact = true;
    }
    if (/^(\\d|\[0-9\])(\+|\{\d+,?\d*\})$/.test(a)) {
      values.push("a long number");
      altExact = true;
      continue;
    }
    const v = unescapeLiteral(a);
    if (v === null || v === "" && !(number && alts.length === 1)) return null;
    if (v !== "") values.push(v);
  }
  const op = start && end ? "is" : start ? "starts" : end ? "ends" : "contains";
  // \s+ and commas don't survive a round trip through the simple form, so those stay pattern-only.
  const editable = !number && !altExact && values.length > 0 && !/\\s/.test(s) && !values.some(v => v.indexOf(",") >= 0);
  return {
    op,
    values,
    number,
    editable
  };
}
const escRe = v => v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
export function buildPattern(op, text) {
  const vals = String(text || "").split(",").map(v => v.trim()).filter(Boolean).map(escRe);
  if (!vals.length) return "";
  const body = vals.length > 1 ? `(${vals.join("|")})` : vals[0];
  return op === "starts" ? `^${body}` : op === "is" ? `^${body}$` : op === "ends" ? `${body}$` : body;
}
const OP_WORDS = {
  is: "is",
  starts: "starts with",
  ends: "ends with",
  contains: "contains"
};
function listWords(values) {
  if (values.length > 5) return `${values.slice(0, 4).join(", ")}, …`;
  if (values.length <= 1) return values[0] || "";
  return `${values.slice(0, -1).join(", ")} or ${values[values.length - 1]}`;
}
function clause(field, pattern) {
  const p = parsePattern(pattern);
  if (!p) return `${field} matches a custom pattern`;
  if (!p.values.length) return `${field} starts with a number`;
  return `${field} ${OP_WORDS[p.op]} ${listWords(p.values)}${p.number ? " and a number" : ""}`;
}
export function ruleSentence(r) {
  const parts = [];
  if (r.skuPattern) parts.push(clause("SKU", r.skuPattern));
  if (r.descPattern) parts.push(clause(parts.length ? "description" : "Description", r.descPattern));
  return parts.length ? parts.join(" or ") : "No pattern yet";
}

// ---- kinds ----------------------------------------------------------------------------------
const KIND_LABEL = {
  frame: "Product",
  ignore: "Ignore",
  nail_head: "Nailhead",
  nailhead: "Nailhead"
};
export const kindLabel = k => KIND_LABEL[k] || String(k || "").replace(/[_-]+/g, " ").replace(/^\w/, c => c.toUpperCase()) || "Not set";
const KIND_TONE = {
  frame: "accent",
  ignore: "neutral",
  fabric: "green",
  leather: "purple",
  finish: "amber",
  color: "amber",
  cushion: "blue",
  size: "blue"
};
const TONES = ["blue", "teal", "amber", "purple", "green", "rose"];
function kindTone(k) {
  if (KIND_TONE[k]) return KIND_TONE[k];
  let h = 0;
  for (const c of String(k || "")) h = h * 31 + c.charCodeAt(0) >>> 0;
  return TONES[h % TONES.length];
}
const KindChip = ({
  kind
}) => /*#__PURE__*/React.createElement("span", {
  className: `dr-kind ${kindTone(kind)}`
}, kindLabel(kind));
const BASE_KINDS = ["frame", "finish", "color", "size", "accessory"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function dayFmt(v, forceYear) {
  if (!v) return "";
  const d = new Date(String(v).length <= 10 ? `${String(v).slice(0, 10)}T12:00:00` : v);
  if (isNaN(d)) return String(v).slice(0, 10);
  return `${MON[d.getMonth()]} ${d.getDate()}${forceYear || d.getFullYear() !== new Date().getFullYear() ? `, ${d.getFullYear()}` : ""}`;
}

// ---- small pieces ---------------------------------------------------------------------------
function RowMenu({
  items
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const off = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, [open]);
  return /*#__PURE__*/React.createElement("span", {
    className: "dr-menu",
    ref: ref
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "dr-more",
    "aria-label": "More actions",
    onClick: () => setOpen(!open)
  }, "\xB7\xB7\xB7"), open ? /*#__PURE__*/React.createElement("span", {
    className: "dr-menu-pop"
  }, items.filter(Boolean).map(it => /*#__PURE__*/React.createElement("button", {
    type: "button",
    key: it.label,
    className: it.danger ? "danger" : "",
    disabled: it.disabled,
    onClick: () => {
      setOpen(false);
      it.onClick();
    }
  }, it.label))) : null);
}
function RuleForm({
  rule,
  kinds,
  previewRule,
  onDone,
  onCancel,
  isNew
}) {
  const origField = rule.skuPattern != null && rule.skuPattern !== "" ? "sku" : rule.descPattern ? "desc" : "sku";
  const origPattern = (origField === "sku" ? rule.skuPattern : rule.descPattern) || "";
  const parsed = parsePattern(origPattern);
  const simpleOk = !origPattern || parsed && parsed.editable;
  const [field, setField] = React.useState(origField);
  const [mode, setMode] = React.useState(simpleOk ? "simple" : "pattern");
  const [op, setOp] = React.useState(parsed && parsed.editable ? parsed.op : "starts");
  const [text, setText] = React.useState(parsed && parsed.editable ? parsed.values.join(", ") : "");
  const [raw, setRaw] = React.useState(origPattern);
  const known = kinds.indexOf(rule.kind) >= 0;
  const [kind, setKind] = React.useState(rule.kind && known ? rule.kind : rule.kind ? "__other" : "frame");
  const [otherKind, setOtherKind] = React.useState(rule.kind && !known ? rule.kind : "");
  const [count, setCount] = React.useState(null); // null | "busy" | "err" | {matched, windowStart, windowEnd}
  const seq = React.useRef(0);
  const pattern = mode === "simple" ? buildPattern(op, text) : raw.trim();
  let valid = !!pattern;
  try {
    if (pattern) new RegExp(pattern.replace(/^\(\?i\)/, ""));
  } catch (e) {
    valid = false;
  }
  const finalKind = kind === "__other" ? otherKind.trim().toLowerCase().replace(/\s+/g, "_") : kind;
  React.useEffect(() => {
    if (!previewRule || !pattern || !valid) {
      setCount(null);
      return undefined;
    }
    const my = ++seq.current;
    setCount("busy");
    const t = setTimeout(() => {
      previewRule({
        kind: finalKind || "frame",
        [field === "sku" ? "skuPattern" : "descPattern"]: pattern
      }).then(r => {
        if (seq.current === my) setCount(r && r.matched != null ? r : null);
      }).catch(() => {
        if (seq.current === my) setCount("err");
      });
    }, 900);
    return () => clearTimeout(t);
  }, [pattern, field, valid]); // eslint-disable-line react-hooks/exhaustive-deps

  const done = () => {
    const out = {
      ...rule,
      kind: finalKind
    };
    if (field === "sku") {
      out.skuPattern = pattern;
      if (origField !== "sku") out.descPattern = undefined;
    } else {
      out.descPattern = pattern;
      if (origField !== "desc") out.skuPattern = undefined;
    }
    onDone(out);
  };
  const toPattern = () => {
    setRaw(pattern || raw);
    setMode("pattern");
  };
  const toSimple = () => {
    const p = parsePattern(raw);
    if (p && p.editable) {
      setOp(p.op);
      setText(p.values.join(", "));
    }
    setMode("simple");
  };
  const fieldWord = field === "sku" ? "SKU" : "description";
  return /*#__PURE__*/React.createElement("div", {
    className: "dr-form"
  }, /*#__PURE__*/React.createElement("label", {
    className: "dr-when"
  }, /*#__PURE__*/React.createElement("span", {
    className: "dr-lbl"
  }, "When the", /*#__PURE__*/React.createElement("select", {
    className: "dr-inline-select",
    value: field,
    onChange: e => setField(e.target.value),
    "aria-label": "Applies to"
  }, /*#__PURE__*/React.createElement("option", {
    value: "sku"
  }, "SKU"), /*#__PURE__*/React.createElement("option", {
    value: "desc"
  }, "description"))), mode === "simple" ? /*#__PURE__*/React.createElement("span", {
    className: "dr-row"
  }, /*#__PURE__*/React.createElement("select", {
    value: op,
    onChange: e => setOp(e.target.value),
    "aria-label": "How it matches"
  }, /*#__PURE__*/React.createElement("option", {
    value: "starts"
  }, "starts with"), /*#__PURE__*/React.createElement("option", {
    value: "is"
  }, "is"), /*#__PURE__*/React.createElement("option", {
    value: "contains"
  }, "contains"), /*#__PURE__*/React.createElement("option", {
    value: "ends"
  }, "ends with")), /*#__PURE__*/React.createElement("input", {
    value: text,
    autoFocus: isNew,
    spellCheck: false,
    placeholder: field === "sku" ? "For example ABC" : "For example sample",
    onChange: e => setText(e.target.value)
  })) : /*#__PURE__*/React.createElement("span", {
    className: "dr-row"
  }, /*#__PURE__*/React.createElement("input", {
    className: "mono",
    value: raw,
    spellCheck: false,
    placeholder: "^ABC\\d+",
    onChange: e => setRaw(e.target.value)
  }))), /*#__PURE__*/React.createElement("label", {
    className: "dr-as"
  }, /*#__PURE__*/React.createElement("span", {
    className: "dr-lbl"
  }, "Count it as"), /*#__PURE__*/React.createElement("select", {
    value: kind,
    onChange: e => setKind(e.target.value)
  }, kinds.map(k => /*#__PURE__*/React.createElement("option", {
    key: k,
    value: k
  }, kindLabel(k))), /*#__PURE__*/React.createElement("option", {
    value: "__other"
  }, "Something else\u2026")), kind === "__other" ? /*#__PURE__*/React.createElement("input", {
    value: otherKind,
    placeholder: "Name it, for example Trim",
    onChange: e => setOtherKind(e.target.value)
  }) : null), /*#__PURE__*/React.createElement("span", {
    className: `dr-count${count && count.matched > 0 ? " ok" : ""}`
  }, !pattern ? "" : !valid ? "This pattern isn't complete yet." : count === "busy" ? "Counting matches…" : count === "err" ? "Couldn't count matches right now." : count && count.matched != null ? count.matched > 0 ? `Matches ${count.matched.toLocaleString("en-US")} lines on orders from ${dayFmt(count.windowStart)} to ${dayFmt(count.windowEnd)}` : `No order lines from ${dayFmt(count.windowStart)} to ${dayFmt(count.windowEnd)} match this ${fieldWord}.` : ""), /*#__PURE__*/React.createElement("span", {
    className: "dr-form-foot"
  }, mode === "simple" ? /*#__PURE__*/React.createElement("span", null, "Separate several with commas. Need something more exact? ", /*#__PURE__*/React.createElement("a", {
    onClick: toPattern
  }, "Write a pattern instead")) : /*#__PURE__*/React.createElement("span", null, "Patterns ignore upper and lower case. ", /*#__PURE__*/React.createElement("a", {
    onClick: toSimple
  }, "Use the simple form")), /*#__PURE__*/React.createElement("span", {
    className: "dr-form-btns"
  }, /*#__PURE__*/React.createElement(Btn, {
    small: true,
    onClick: onCancel
  }, "Cancel"), /*#__PURE__*/React.createElement(Btn, {
    small: true,
    primary: true,
    disabled: !valid || !finalKind,
    onClick: done
  }, isNew ? "Add rule" : "Done"))));
}
const PAIRING = [["same_order", "On the same order", "Options belong to the products they were ordered with."], ["linked_field", "By a shared field", "Options and products share a value in your sales file, like a reference number."], ["none", "Don't attach", "Count options on their own."]];

// ---- the tab --------------------------------------------------------------------------------
export default function RulesKit(p) {
  const {
    meta,
    rules,
    pairing,
    setPairing,
    seriesKey,
    setSeriesKey,
    dirty,
    setDirty,
    report,
    setReport,
    drafts,
    setDrafts,
    note,
    setNote,
    revert,
    move,
    editRule,
    removeRule,
    addRule,
    load,
    msgOf,
    KIND_COLORS,
    num,
    segs,
    seriesHidden,
    kitPreview,
    kitPreviewRule,
    kitSuggest,
    kitSave,
    kitRebuild
  } = p;
  const [editing, setEditing] = React.useState(null); // index | "new" | null
  const [work, setWork] = React.useState(null); // null | "test" | "suggest" | "save" | "recalc"
  const [confirm, setConfirm] = React.useState(false);
  const [tone, setTone] = React.useState("info");
  const [showVersions, setShowVersions] = React.useState(false);
  const [showMore, setShowMore] = React.useState(false);
  const alive = React.useRef(true);
  React.useEffect(() => () => {
    alive.current = false;
  }, []);
  const say = (text, t = "info") => {
    setTone(t);
    setNote(text);
  };
  const kinds = Array.from(new Set(["frame", ...rules.map(r => r.kind).filter(Boolean), ...BASE_KINDS.slice(1), "ignore"]));
  const test = () => {
    setWork("test");
    setNote(null);
    kitPreview().then(rep => {
      if (!alive.current) return;
      setReport(rep);
      setWork(null);
    }).catch(e => {
      if (!alive.current) return;
      setWork(null);
      say(msgOf(e, "The test didn't run. Check that every rule is complete."), "danger");
    });
  };
  const suggest = () => {
    setWork("suggest");
    setNote(null);
    kitSuggest().then(d => {
      if (!alive.current) return;
      setDrafts(d && d.rules || []);
      setWork(null);
    }).catch(e => {
      if (!alive.current) return;
      setWork(null);
      say(msgOf(e, "Suggestions aren't available right now. Try again in a moment."), "danger");
    });
  };
  const saveAndRecalc = () => {
    setConfirm(false);
    setEditing(null);
    setNote(null);
    setWork("save");
    const saveStep = dirty ? kitSave() : Promise.resolve(null);
    saveStep.then(() => {
      if (!alive.current) return null;
      if (dirty) load();
      setWork("recalc");
      return kitRebuild().then(d => {
        if (!alive.current) return;
        setWork(null);
        say(`Saved and recalculated. ${num(d && d.written)} rows on the Options and Series pages now use these rules. Older history catches up overnight.`);
      }).catch(e => {
        if (!alive.current) return;
        setWork(null);
        say(`Your rules are saved, but the recalculation didn't finish (${msgOf(e, "no details")}). It runs again overnight, or try again.`, "warning");
      });
    }).catch(e => {
      if (!alive.current) return;
      setWork(null);
      say(msgOf(e, "Your rules couldn't be saved. Check that every rule is complete."), "danger");
    });
  };
  const busy = !!work;
  const actions = confirm ? /*#__PURE__*/React.createElement("span", {
    className: "dr-confirm"
  }, /*#__PURE__*/React.createElement("span", null, dirty ? "Save these rules and recalculate the Options and Series pages?" : "Recalculate the Options and Series pages with the saved rules?", " It takes a few minutes."), /*#__PURE__*/React.createElement(Btn, {
    primary: true,
    onClick: saveAndRecalc
  }, dirty ? "Yes, save" : "Yes, recalculate"), /*#__PURE__*/React.createElement(Btn, {
    onClick: () => setConfirm(false)
  }, "Cancel")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Btn, {
    disabled: busy,
    onClick: test
  }, work === "test" ? "Testing…" : "Test on recent orders"), /*#__PURE__*/React.createElement(Btn, {
    primary: true,
    disabled: busy || editing !== null,
    title: editing !== null ? "Finish the rule you're editing first" : undefined,
    onClick: () => setConfirm(true)
  }, work === "save" ? "Saving…" : work === "recalc" ? "Recalculating…" : "Save and recalculate"));
  const history = meta && meta.history || [];
  const changed = meta && meta.updatedAt ? `Last changed ${dayFmt(meta.updatedAt)}${meta.updatedBy ? ` by ${meta.updatedBy}` : ""}.` : "";
  const finishEdit = (i, r) => {
    if (i === "new") addRule(r);else editRule(i, r);
    setEditing(null);
  };
  const formFor = i => /*#__PURE__*/React.createElement("tr", {
    className: "dr-form-row",
    key: `form-${i}`
  }, /*#__PURE__*/React.createElement("td", {
    colSpan: 4
  }, /*#__PURE__*/React.createElement(RuleForm, {
    rule: i === "new" ? {
      kind: "frame",
      skuPattern: ""
    } : rules[i],
    isNew: i === "new",
    kinds: kinds,
    previewRule: kitPreviewRule,
    onDone: r => finishEdit(i, r),
    onCancel: () => setEditing(null)
  })));
  const shown = report && !report.empty ? segs.filter(([, v]) => Number(v) > 0) : [];
  return /*#__PURE__*/React.createElement(DataShell, {
    active: "/Data/Rules",
    subtitle: "How Plunk sorts the lines on your orders",
    actions: actions,
    cls: "wa-editor dr-view"
  }, work === "save" || work === "recalc" ? /*#__PURE__*/React.createElement(Notice, null, /*#__PURE__*/React.createElement("span", {
    className: "grow"
  }, work === "save" ? "Saving your rules…" : "Recalculating the Options and Series pages. This can take a few minutes; you can keep working.")) : null, note ? /*#__PURE__*/React.createElement(Notice, {
    tone: tone,
    onClose: () => setNote(null)
  }, /*#__PURE__*/React.createElement("span", {
    className: "grow"
  }, note)) : null, /*#__PURE__*/React.createElement("div", {
    className: "wa-grid"
  }, /*#__PURE__*/React.createElement(Card, {
    span: 12,
    cls: "dr-explain"
  }, /*#__PURE__*/React.createElement("b", {
    className: "dr-explain-title"
  }, "How Plunk reads your order lines"), /*#__PURE__*/React.createElement("p", null, "Each line on an order is a product, an option on a product, or something to ignore, like shipping. Plunk checks these rules from top to bottom and uses the first one that fits. Saving recalculates the Options and Series pages, which takes a few minutes."), changed || history.length ? /*#__PURE__*/React.createElement("p", {
    className: "dr-meta"
  }, changed, history.length ? /*#__PURE__*/React.createElement(React.Fragment, null, " ", /*#__PURE__*/React.createElement("a", {
    onClick: () => setShowVersions(!showVersions)
  }, showVersions ? "Hide earlier versions" : "Earlier versions")) : null) : null, showVersions ? /*#__PURE__*/React.createElement("ul", {
    className: "dr-versions"
  }, history.map((h, i) => /*#__PURE__*/React.createElement("li", {
    key: i
  }, /*#__PURE__*/React.createElement("span", null, "Version ", (meta.version || 0) - i - 1), /*#__PURE__*/React.createElement("span", {
    className: "dim"
  }, (h && h.lineRules || []).length, " rules"), /*#__PURE__*/React.createElement("a", {
    onClick: () => {
      revert(h);
      setEditing(null);
      say("That version is in the editor. Review it, then Save and recalculate to use it.");
    }
  }, "Load into editor")))) : null), /*#__PURE__*/React.createElement(Card, {
    span: 12,
    cls: "dr-rules",
    title: "Rules",
    right: dirty ? /*#__PURE__*/React.createElement("span", {
      className: "dr-unsaved"
    }, "Unsaved changes") : null
  }, rules.length || editing === "new" ? /*#__PURE__*/React.createElement("div", {
    className: "tblwrap"
  }, /*#__PURE__*/React.createElement("table", {
    className: "territory-table wa-table dr-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
    className: "dr-ord"
  }, "Order"), /*#__PURE__*/React.createElement("th", null, "Rule"), /*#__PURE__*/React.createElement("th", {
    className: "dr-as-col"
  }, "Counts as"), /*#__PURE__*/React.createElement("th", {
    className: "dr-act"
  }))), /*#__PURE__*/React.createElement("tbody", null, rules.map((r, i) => editing === i ? formFor(i) : /*#__PURE__*/React.createElement("tr", {
    key: i
  }, /*#__PURE__*/React.createElement("td", {
    className: "dr-ord dim"
  }, i + 1), /*#__PURE__*/React.createElement("td", {
    className: "wrap",
    title: [r.skuPattern, r.descPattern].filter(Boolean).join("  ·  ")
  }, ruleSentence(r)), /*#__PURE__*/React.createElement("td", {
    className: "dr-as-col"
  }, /*#__PURE__*/React.createElement(KindChip, {
    kind: r.kind
  })), /*#__PURE__*/React.createElement("td", {
    className: "dr-act"
  }, /*#__PURE__*/React.createElement("a", {
    className: "wa-rowlink",
    onClick: () => !busy && setEditing(i)
  }, "Edit"), /*#__PURE__*/React.createElement(RowMenu, {
    items: [{
      label: "Move up",
      disabled: i === 0 || busy || editing !== null,
      onClick: () => move(i, -1)
    }, {
      label: "Move down",
      disabled: i === rules.length - 1 || busy || editing !== null,
      onClick: () => move(i, 1)
    }, {
      label: "Remove",
      danger: true,
      disabled: busy || editing !== null,
      onClick: () => {
        removeRule(i);
        if (typeof editing === "number" && editing > i) setEditing(editing - 1);
      }
    }]
  })))), editing === "new" ? formFor("new") : null))) : /*#__PURE__*/React.createElement(Empty, null, "No rules yet. Add one, or let Plunk suggest rules from your recent orders."), /*#__PURE__*/React.createElement("div", {
    className: "dr-rule-actions"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "dr-add",
    disabled: busy || editing !== null,
    onClick: () => setEditing("new")
  }, "+ Add a rule"), /*#__PURE__*/React.createElement("a", {
    className: busy ? "disabled" : "",
    onClick: () => !busy && suggest()
  }, work === "suggest" ? "Looking at your orders…" : "Suggest rules"))), drafts ? /*#__PURE__*/React.createElement(Card, {
    span: 12,
    title: "Suggested rules",
    hint: "nothing changes until you add them"
  }, drafts.length === 0 ? /*#__PURE__*/React.createElement(Empty, null, "No suggestions for your recent orders.") : /*#__PURE__*/React.createElement("div", {
    className: "tblwrap"
  }, /*#__PURE__*/React.createElement("table", {
    className: "territory-table wa-table dr-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Rule"), /*#__PURE__*/React.createElement("th", {
    className: "dr-as-col"
  }, "Counts as"), /*#__PURE__*/React.createElement("th", null, "Why"), /*#__PURE__*/React.createElement("th", {
    className: "dr-act"
  }))), /*#__PURE__*/React.createElement("tbody", null, drafts.map((d, i) => /*#__PURE__*/React.createElement("tr", {
    key: i
  }, /*#__PURE__*/React.createElement("td", {
    className: "wrap"
  }, ruleSentence(d)), /*#__PURE__*/React.createElement("td", {
    className: "dr-as-col"
  }, /*#__PURE__*/React.createElement(KindChip, {
    kind: d.kind
  })), /*#__PURE__*/React.createElement("td", {
    className: "wrap dim"
  }, d.rationale), /*#__PURE__*/React.createElement("td", {
    className: "dr-act"
  }, /*#__PURE__*/React.createElement("a", {
    className: "wa-rowlink",
    onClick: () => {
      addRule({
        kind: d.kind,
        skuPattern: d.skuPattern,
        descPattern: d.descPattern
      });
      setDrafts(drafts.filter((x, j) => j !== i));
    }
  }, "Add"))))))), /*#__PURE__*/React.createElement("div", {
    className: "dr-rule-actions"
  }, drafts.length > 0 ? /*#__PURE__*/React.createElement(Btn, {
    small: true,
    onClick: () => {
      drafts.forEach(d => addRule({
        kind: d.kind,
        skuPattern: d.skuPattern,
        descPattern: d.descPattern
      }));
      setDrafts(null);
    }
  }, "Add all") : null, /*#__PURE__*/React.createElement(Btn, {
    small: true,
    onClick: () => setDrafts(null)
  }, "Dismiss"))) : null, report && !report.empty ? /*#__PURE__*/React.createElement(Card, {
    span: 12,
    title: "Test on recent orders",
    hint: `orders from ${dayFmt(report.windowStart, true)} to ${dayFmt(report.windowEnd, true)}`,
    right: /*#__PURE__*/React.createElement("a", {
      onClick: () => setReport(null)
    }, "Close")
  }, /*#__PURE__*/React.createElement("div", {
    className: "cov-bar"
  }, shown.map(([k, v]) => /*#__PURE__*/React.createElement("div", {
    key: k,
    title: `${k === "unmatched" ? "No rule" : kindLabel(k)}: ${num(v)}`,
    style: {
      width: `${100 * Number(v) / Math.max(1, report.linesTotal)}%`,
      background: KIND_COLORS[k] || "#ccc"
    }
  }))), /*#__PURE__*/React.createElement("div", {
    className: "cov-legend"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, num(report.linesTotal)), " order lines"), shown.map(([k, v]) => /*#__PURE__*/React.createElement("span", {
    key: k
  }, /*#__PURE__*/React.createElement("i", {
    style: {
      background: KIND_COLORS[k] || "#ccc"
    }
  }), k === "unmatched" ? "No rule fits" : kindLabel(k), " ", num(v)))), report.strategy !== "none" ? /*#__PURE__*/React.createElement("p", {
    className: "dr-test-line"
  }, num(report.pairedComponentLines), " of ", num(report.componentLines), " option lines were attached to a product. ", num(report.styles), " products and ", num(report.distinctComponents), " different options.") : null, report.unpairedReason ? /*#__PURE__*/React.createElement(Notice, {
    tone: "warning"
  }, /*#__PURE__*/React.createElement("span", {
    className: "grow"
  }, report.unpairedReason)) : null, (report.topUnmatchedShapes || []).length > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", {
    className: "dr-test-line"
  }, "Lines no rule fits (# stands for any digit):"), /*#__PURE__*/React.createElement("div", {
    className: "tblwrap"
  }, /*#__PURE__*/React.createElement("table", {
    className: "territory-table wa-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "SKU looks like"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Lines"))), /*#__PURE__*/React.createElement("tbody", null, report.topUnmatchedShapes.map((s, i) => /*#__PURE__*/React.createElement("tr", {
    key: i
  }, /*#__PURE__*/React.createElement("td", {
    className: "mono"
  }, s.shape), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, num(s.count)))))))) : null) : null, report && report.empty ? /*#__PURE__*/React.createElement(Card, {
    span: 12,
    title: "Test on recent orders"
  }, /*#__PURE__*/React.createElement(Empty, null, report.message || "There are no orders to test against yet.")) : null, /*#__PURE__*/React.createElement(Card, {
    span: 12,
    cls: "dr-more-card",
    title: "More settings",
    right: /*#__PURE__*/React.createElement("a", {
      onClick: () => setShowMore(!showMore)
    }, showMore ? "Hide" : "Show")
  }, showMore ? /*#__PURE__*/React.createElement("div", {
    className: "dr-more-grid"
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", {
    className: "dr-sub"
  }, "How options attach to products"), PAIRING.map(([key, label, help]) => /*#__PURE__*/React.createElement("label", {
    key: key,
    className: "dr-radio"
  }, /*#__PURE__*/React.createElement("input", {
    type: "radio",
    name: "pairing",
    checked: pairing.strategy === key,
    onChange: () => {
      setPairing({
        ...pairing,
        strategy: key
      });
      setDirty(true);
    }
  }), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, label), /*#__PURE__*/React.createElement("span", {
    className: "dim"
  }, help)))), pairing.strategy === "linked_field" ? /*#__PURE__*/React.createElement("label", {
    className: "dr-field"
  }, "Field they share", /*#__PURE__*/React.createElement("input", {
    className: "mono",
    value: pairing.linkedField,
    placeholder: "coupon, customer_id, store_id, order_status or metadata:<key>",
    onChange: e => {
      setPairing({
        ...pairing,
        linkedField: e.target.value
      });
      setDirty(true);
    }
  })) : null), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", {
    className: "dr-sub"
  }, "Series grouping"), /*#__PURE__*/React.createElement("p", {
    className: "dim"
  }, "Leave blank and Plunk groups related SKUs into a series on its own. To set it yourself, write a pattern whose first part in parentheses is the series code."), /*#__PURE__*/React.createElement("label", {
    className: "dr-field"
  }, "Series pattern", /*#__PURE__*/React.createElement("input", {
    className: "mono",
    value: seriesKey,
    placeholder: "^[A-Za-z]*([0-9]+)",
    onChange: e => {
      setSeriesKey(e.target.value);
      setDirty(true);
      setNote(null);
    }
  })), /*#__PURE__*/React.createElement("p", {
    className: "dim"
  }, "For example, ", /*#__PURE__*/React.createElement("code", null, "^[A-Za-z]*([0-9]+)"), " puts AB100-1 and C100-2 in series 100."), /*#__PURE__*/React.createElement("p", {
    className: "dim"
  }, "The Series page is ", seriesHidden ? "hidden for this workspace" : "shown", ". Your Plunk administrator manages that."))) : /*#__PURE__*/React.createElement("p", {
    className: "dim dr-more-hint"
  }, "How options attach to products, and how SKUs group into a series."))));
}